import { Option } from '../components/molecules/Invoices/InvoiceCardLinkBoard'
import { InstallmentInfo } from '../components/organisms/Cart/interfaces'
import {
  CartItem,
  InvoiceSummary,
  Product,
  Rating,
  ScheduledDeliveryType,
} from '../store/data/types'
import { MerchantStatus } from '../store/slices/session'
import {
  BLOCKED_EDITION_SUPPLIERS,
  CLUBBI_SUPPLIER,
  CX_WHATSAPP_LINK,
  DAYS_TO_DELIVERY_RULES,
  DELIVERY_TYPES,
  DELIVERY_TYPE_ENUM,
  INSTALLMENT_RULES,
  PRICING_GROUP_IDS_IN_GET_MORE_FOR_LESS,
  blockingStatuses,
  whatsappHelpLinkGenerator,
} from './constants'

export const cpfMask = (value: string) => {
  return value
    .replace(/\D/g, '') // substitui qualquer caracter que nao seja numero por nada
    .replace(/(\d{5})\d+?$/, '$1') // captura 5 numeros e não deixa ser digitado mais nada
}

export const getDateFromISO = (datetime: string): string => {
  return datetime.split('T')[0]
}

export const formatDateDayMonth = (date?: string, withYear?: boolean): string => {
  if (!date) {
    return ''
  }
  const dayMonth = date.slice(8) + '/' + date.slice(5, 7)
  if (withYear) {
    return dayMonth + '/' + date.slice(2, 4)
  }
  return dayMonth
}

export const weekDayMapping: { [index: number]: string } = {
  0: 'domingo',
  1: 'segunda-feira',
  2: 'terça-feira',
  3: 'quarta-feira',
  4: 'quinta-feira',
  5: 'sexta-feira',
  6: 'sábado',
}

export const checkDelayedPayment = (billingStatus: string) => {
  return billingStatus === 'OVERDUE' || billingStatus === 'DUNNING_REQUESTED'
}

export const filterInvoicesByBillingStatus = (invoices: InvoiceSummary[], getOverdue: boolean) => {
  return invoices.filter(({ billingStatus }) => checkDelayedPayment(billingStatus) === getOverdue)
}

export const isMerchantBlocked = (status: MerchantStatus | undefined) =>
  blockingStatuses.some((blockingStatus) => status === blockingStatus)

export const proveSourceWebhook = async (data: any) => {
  const messageUrl = 'https://api.provesrc.com/webhooks/track/98dc2ef1601542ed48e6721267947a65'
  const response = await fetch(messageUrl, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.json()
}

export const statusTranslator = (status: string) => {
  switch (status) {
    case 'DELIVERY_ACCEPTED':
      return 'Entrega realizada'
    case 'RECEIVED':
      return 'Em separação'
    case 'PICKING':
      return 'Pedido em expedição'
    case 'PICKED':
      return 'Saiu para entrega'
    case 'IN ROUTE':
      return 'Entregador está próximo'
    case 'DELIVERY_NOT_ACCEPTED':
      return 'Entrega não realizada'
    default:
      return 'Em preparação'
  }
}

export const paymentTypeTranslator = (paymentType: number) => {
  switch (paymentType) {
    case 0:
      return 'Boleto'
    case 1:
      return 'Cartão de crédito'
    case 2:
      return 'Pix'
    case 3:
      return 'Transferência'
    case 4:
      return 'Depósito'
    default:
      return 'unknown'
  }
}

export const billingStatusTranslator = (status: string) => {
  switch (status) {
    case 'PENDING':
      return 'Aguardando pagamento'
    case 'RECEIVED':
      return 'Pagamento confirmado'
    case 'CONFIRMED':
      return 'Pagamento confirmado'
    case 'OVERDUE':
      return 'Pagamento atrasado'
    case 'DUNNING_REQUESTED':
      return 'Pagamento atrasado'
    case 'REFUNDED':
      return 'Pagamento estornado'
    default:
      return 'Aguardando geração do boleto'
  }
}

export const dateFromTimestamp = (dateString: string) => {
  const weekdayMapping: { [index: number]: string } = {
    0: 'Domingo',
    1: 'Segunda-feira',
    2: 'Terça-feira',
    3: 'Quarta-feira',
    4: 'Quinta-feira',
    5: 'Sexta-feira',
    6: 'Sábado',
  }
  const dateObject = new Date(dateString)
  const weekday = dateObject.getDay()
  const day = dateObject.getDate()
  const month = dateObject.getMonth()
  const year = dateObject.getFullYear()

  return `${weekdayMapping[weekday]}, ${day}/${month + 1}/${year}`
}

export const groupInvoicesByDate = (invoices: InvoiceSummary[]) => {
  const groupedInvoices: { [index: string]: InvoiceSummary[] } = {}

  invoices.forEach((invoice) => {
    const timestamp = Date.parse(invoice.creationDatetime)
    const dateObject = new Date(timestamp)
    dateObject.setHours(0, 0, 0, 0)
    const dateString = dateObject.toISOString()
    if (groupedInvoices[dateString]) {
      groupedInvoices[dateString].push(invoice)
    } else {
      groupedInvoices[dateString] = [invoice]
    }
  })

  return groupedInvoices
}

export const checkString = (url?: string | null) => url === '' || url === null || url === undefined

export const generateLinkOptionsFromInvoice = (
  invoice: InvoiceSummary,
  merchantCode?: string,
  fromDeliverySummary?: boolean
) => {
  const { shopperPlanId, billingStatus, url, leftForDeliveryDatetime, nfeUrls } = invoice

  const subOptions: Option[] =
    nfeUrls.length > 0
      ? nfeUrls.map((url, i) => ({
          label: url.includes('amazon') ? `Abrir Nota Fiscal ${i + 1}` : 'Cupom Fiscal',
          url,
          disabled: false,
          subOptions: [],
        }))
      : []

  const addFromDeliverySummaryParam = (baseUrl: string): string => {
    if (!fromDeliverySummary) return baseUrl
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}fromDeliverySummary=true`
  }

  const purchaseUrl = addFromDeliverySummaryParam(`/purchase${url}`)
  const deliveryUrl = addFromDeliverySummaryParam(`/delivery${url}`)

  const options: Option[] = [
    { label: 'Pedido', url: purchaseUrl, disabled: checkString(url), subOptions: [] },
    {
      label: 'Fatura',
      url: leftForDeliveryDatetime ? deliveryUrl : leftForDeliveryDatetime,
      disabled: checkString(url) || checkString(leftForDeliveryDatetime),
      subOptions: [],
    },
    { label: 'Doc. Fiscal', subOptions: subOptions, disabled: nfeUrls.length <= 0 },
    {
      label: 'Ajuda',
      url: checkDelayedPayment(billingStatus)
        ? whatsappHelpLinkGenerator(merchantCode, shopperPlanId)
        : CX_WHATSAPP_LINK,
      disabled: false,
      subOptions: [],
    },
  ]

  return options
}

const DEADLINE_TO_EVALUATE_DELIVERY = 60 // 60 days

export const isRatingDisabled = (rating: Rating) => {
  const creationDate = new Date(rating.creationDatetime)
  const currentDate = new Date()
  const timeDifference = currentDate.getTime() - creationDate.getTime()
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24)

  return daysDifference > DEADLINE_TO_EVALUATE_DELIVERY
}

const debouncePromise = (fn: any, time: any) => {
  let timerId: any = undefined

  return (...args: any[]) => {
    if (timerId) {
      clearTimeout(timerId)
    }

    return new Promise((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time)
    })
  }
}

export const debounced = debouncePromise((items: any) => Promise.resolve(items), 500)

export const pad = (num: number, size: number): string => {
  let numStr: string = num.toString()
  while (numStr.length < size) numStr = '0' + numStr
  return numStr
}

export const getDeadlineMessage = (paymentDeadline: string | null) =>
  `Seu prazo para pagar é de ${paymentDeadline} dias após o recebimento.`

export const installmentsMessage = (paymentType: number) => {
  const type = paymentTypeTranslator(paymentType)
  return `Nas compras por ${type.toLocaleLowerCase()} parcelado o valor total do pedido é divido em várias parcelas, facilitando seu fluxo de caixa!`
}

export const getShippingInfos = (
  type: DELIVERY_TYPE_ENUM,
  isScheduleEnabled: boolean,
  scheduledDelivery: ScheduledDeliveryType
) => {
  if (isScheduleEnabled) {
    return {
      value: scheduledDelivery.fee ?? 0,
      daysToDelivery: scheduledDelivery.daysToDelivery ?? 0,
    }
  }

  if (type === DELIVERY_TYPE_ENUM.twoDays) {
    return DELIVERY_TYPES.twoDays
  }
  return DELIVERY_TYPES.fast
}

export const getInstallmentsOptions = (
  paymentDeadline: string | undefined,
  paymentType: number,
  isInstantPayment: boolean | undefined
) => {
  const rule = INSTALLMENT_RULES[paymentDeadline ?? '']
  if (!rule || isInstantPayment)
    return [
      {
        installment: 1,
        days: paymentDeadline,
      },
    ]

  const options = [
    {
      installment: 1,
      days: String(rule.oneTime),
    },
  ]

  if (paymentType !== 1) {
    // Pix / Boleto: permite 3x
    options.push({
      installment: 3,
      days: rule.threeTimes.join(', '),
    })
  }
  return options
}

export const getInstallmentsInfo = (
  paymentDeadline: string | undefined,
  numberOfInstallments: number,
  totalValue: number
): InstallmentInfo[] => {
  const rule = INSTALLMENT_RULES[paymentDeadline ?? '']

  if (!rule)
    return [
      {
        installment: 1,
        days: Number(paymentDeadline),
        value: parseFloat(totalValue.toFixed(2)),
      },
    ]

  if (numberOfInstallments === 1) {
    return [
      {
        installment: 1,
        days: rule.oneTime,
        value: parseFloat(totalValue.toFixed(2)),
      },
    ]
  }

  const days = rule.threeTimes
  const baseValue = parseFloat((totalValue / 3).toFixed(2))
  const totalRounded = baseValue * 3
  const difference = parseFloat((totalValue - totalRounded).toFixed(2))
  const firstValue = parseFloat((baseValue + difference).toFixed(2))

  return days.map((day, index) => ({
    installment: index + 1,
    days: day,
    value: index === 0 ? firstValue : baseValue,
  }))
}

export const getNowInSaoPaulo = (): Date => {
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const parts = formatter.formatToParts(now)
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10)

  return new Date(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  )
}

export const getDaysToDelivery = (): number => {
  const now = getNowInSaoPaulo()

  const regionTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 15, 30)
  const period: 'first' | 'second' = now.getTime() < regionTime.getTime() ? 'first' : 'second'

  const jsWeekday = now.getDay() // 0=Segunda, 6=Domingo
  const pyWeekday = (jsWeekday + 6) % 7

  return DAYS_TO_DELIVERY_RULES[period][pyWeekday] ?? 2
}

export const getDeliveryDate = (): string => {
  const days = getDaysToDelivery()
  const now = getNowInSaoPaulo()

  const deliveryDate = new Date(now)
  deliveryDate.setDate(deliveryDate.getDate() + days)

  const date = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(deliveryDate)

  const clean = date.replace('-feira', '')
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}

export const stringToArrayEan = (list: string) => {
  const dataArray = list.split(/\n/)
  const eansArray = dataArray.map((data) => {
    if (data.includes('http')) {
      const ean = data.split('/').pop()
      if (ean) return ean
    }
    return data
  })
  return eansArray
}

export const currentDateString = new Date().toLocaleString('pt-BR', {
  timeZone: 'America/Sao_Paulo',
})

export const getDayOfWeek = (date: Date) => {
  const days = [
    'Domingo',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
  ]
  return days[date.getDay()]
}

export const getLongDate = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: '2-digit' }
  return date.toLocaleDateString('pt-BR', options)
}

export const sortProducts = (products: Product[], eans: string[]) => {
  products.sort((a, b) => {
    const indexA = eans.indexOf(a.ean)
    const indexB = eans.indexOf(b.ean)

    return indexA - indexB
  })

  return products
}

export const showPrices = (merchantCode: string | undefined, cameFromAdsFlag: boolean) => {
  return !!merchantCode || cameFromAdsFlag
}

export const getEconomy = (item: CartItem) => {
  const { getMoreForLess } = item.product.supplierPrices[0]
  if (getMoreForLess) {
    const { priceWithoutPromotion, quantityToGet, promotionalPrice } = getMoreForLess
    const economyCalc = priceWithoutPromotion * quantityToGet - promotionalPrice * quantityToGet
    return economyCalc
  }
  return 0
}

export const quantityToCompleteComboCalc = (item: CartItem | undefined) => {
  if (!item) return 0

  const quantityToGet = item.product.supplierPrices[0].getMoreForLess?.quantityToGet
  if (!quantityToGet) return 0

  const quantity = item.quantity
  const mod = quantity % quantityToGet

  if (quantity > quantityToGet && mod !== 0) return quantityToGet - mod
  return mod === 0 ? 0 : quantityToGet - quantity
}

export const isGetMoreForLess = (
  getMoreForLess: Record<string, unknown> | null,
  pricingGroupId: number | undefined
) => {
  const pricingGroupValid = PRICING_GROUP_IDS_IN_GET_MORE_FOR_LESS.includes(pricingGroupId ?? 0)

  return getMoreForLess !== null && pricingGroupValid
}

export const isBlockedEditSupplier = (supplier: string | null) =>
  BLOCKED_EDITION_SUPPLIERS.includes(supplier ?? CLUBBI_SUPPLIER)

export const formatNumber = (value: number): string => {
  if (value) {
    let formattedValue = value.toFixed(2)
    return (formattedValue = formattedValue.replace('.', ','))
  }
  return ''
}

export const EXTERNAL_CLUBBER = 'Externo'

export const isInternalClubber = (
  clubberEmail: string | undefined,
  clubberArea: string | undefined
) => {
  if (clubberEmail) {
    return clubberArea !== EXTERNAL_CLUBBER
  }
  return false
}

export const isExternalClubber = (clubberArea: string | undefined) => {
  return clubberArea == EXTERNAL_CLUBBER
}

export const isWithinDateRange = (startDate: string, endDate: string) => {
  const today = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)
  return today >= start && today <= end
}

export const getProductDescription = (productDescription: string, isNearExpiry: boolean | null) =>
  isNearExpiry ? `${productDescription} - (Item próximo da validade)` : productDescription

export const START_TWO_DAYS_TO_DELIVERY = '2024-11-18 00:01:00'
export const END_TWO_DAYS_TO_DELIVERY = '2024-11-29 23:59:00'

export const START_BLACK_FRIDAY = '2025-11-03 00:01:00'
export const END_BLACK_FRIDAY = '2025-11-28 23:59:00'

const blackFridayOfertaoInfos = {
  spoiler: {
    title: '🔍 Spoiler Black chegou!!',
    description: 'Vem aí as maiores ofertas do ano 👀',
    start: new Date('2025-11-03 00:01:00'),
    end: new Date('2025-11-09 23:59:00'),
  },
  esquenta: {
    title: '🔥 Esquentaaa!',
    description: 'Aproveite o Ofertão Esquenta Black Friday!',
    start: new Date('2025-11-10 00:01:00'),
    end: new Date('2025-11-16 23:59:00'),
  },
  preBlackFriday: {
    title: '⚡ Tá chegando a hora!!',
    description: 'Pré-Black Clubbi já começou 🚀',
    start: new Date('2025-11-17 00:01:00'),
    end: new Date('2025-11-23 23:59:00'),
  },
  weekBlackFriday: {
    title: '🤑 Semana da Black',
    description: 'O dia mais aguardado do ano está chegando!',
    start: new Date('2025-11-24 00:01:00'),
    end: new Date('2025-11-27 23:59:00'),
  },
  bigDay: {
    title: '💥 CHEGOU!',
    description: 'É o GRANDE DIA DA BLACK FRIDAY!',
    start: new Date('2025-11-28 00:01:00'),
    end: new Date('2025-11-28 23:59:00'),
  },
}

export const getInfosBlackFriday = () => {
  const today = new Date()
  const infos = Object.values(blackFridayOfertaoInfos).find(
    (info) => today >= info.start && today <= info.end
  )
  return infos || null
}

export const isClubbiAnniversary = () => {
  const today = new Date()
  const start = new Date('2025-08-04 05:00:00')
  const end = new Date('2025-09-14 23:59:00')
  return today >= start && today <= end
}

export const anniversaryColor = {
  yellow: '#FFC200',
  deepPurple: '#4600A2',
  red: '#F21F52',
}

export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
  }
}
